import { h } from 'preact';
import { useState, useEffect } from 'preact/hooks';
import { api } from '../state/auth.js';
import { translateError } from '../i18n.js';
import Card from '../components/shared/Card.jsx';
import Button from '../components/shared/Button.jsx';
import Modal from '../components/shared/Modal.jsx';
import AiAssist from '../components/shared/AiAssist.jsx';
import { fileLinkClick } from '../utils/openFile.js';

// Visie & doelen: where the club is heading (vision, mission, core values, policy
// plan) and the focus areas and goals that get it there. Everyone in the club can
// read it; the "Beleid & doelen" permission may edit.

const ERRORS = {
  title_required: 'Geef een titel op.', invalid_vision: 'De visie is te lang (max. 2000 tekens).',
  invalid_mission: 'De missie is te lang (max. 2000 tekens).', invalid_core_values: 'Maximaal 10 kernwaarden van elk max. 100 tekens.',
  invalid_plan_title: 'De titel van het beleidsplan is te lang.', invalid_plan_period: 'Kies een geldige periode (eindjaar niet vóór beginjaar).',
  invalid_document: 'Kies een document van deze club.', invalid_success_factor: 'Kies een succesfactor uit de lijst.',
  invalid_description: 'De omschrijving is te lang.', has_goals: 'Dit speerpunt heeft nog doelen. Verplaats of verwijder die eerst.',
  invalid_focus_area: 'Kies een speerpunt.', invalid_owner: 'Kies een sectie of commissie van deze club.',
  invalid_status: 'Kies een status.', invalid_status_note: 'De toelichting is te lang (max. 1000 tekens).',
  invalid_date: 'De streefdatum klopt niet.', invalid_measure: 'Controleer de meetwaarden (getallen).',
  forbidden: 'Je mag dit niet bewerken.', not_found: 'Dit bestaat niet (meer).',
  invalid_contact: 'De contactpersoon moet een actief lid van de club zijn.', goal_stopped: 'Dit doel is gestopt; pak het eerst weer op.',
};
const message = err => ERRORS[err.code] || translateError(err.code) || 'Dat lukte niet.';

export const GOAL_STATUS = {
  op_schema: { label: 'Op schema', color: '#16a34a', bg: '#dcfce7' },
  aandacht: { label: 'Aandacht', color: '#d97706', bg: '#fef3c7' },
  achter: { label: 'Achter', color: '#dc2626', bg: '#fee2e2' },
  bereikt: { label: 'Bereikt', color: '#2563eb', bg: '#dbeafe' },
};

// Progress of a measure from baseline to target, or null without a full measure
export function measurePct(g) {
  const { baseline_value: b, target_value: t, current_value: c } = g;
  if ([b, t, c].some(v => v === null || v === undefined) || t === b) return null;
  return Math.max(0, Math.min(100, Math.round(((c - b) / (t - b)) * 100)));
}

const fmtNum = v => (v === null || v === undefined ? '–' : Number(v).toLocaleString('nl-NL'));
const fmtDate = d => (d ? new Date(`${d}T12:00:00`).toLocaleDateString('nl-NL', { day: 'numeric', month: 'short', year: 'numeric' }) : null);

function StatusPill({ status }) {
  const s = GOAL_STATUS[status] || GOAL_STATUS.op_schema;
  return <span style={`font-size:11px;font-weight:600;padding:2px 8px;border-radius:10px;background:${s.bg};color:${s.color};white-space:nowrap`}>{s.label}</span>;
}

function Field({ label, children }) {
  return <label class="form-label">{label}{children}</label>;
}

function FormActions({ saving, error, onCancel, onSave }) {
  return (
    <>
      {error && <div class="alert alert-error">{error}</div>}
      <div style="display:flex;gap:8px;justify-content:flex-end">
        <Button variant="secondary" onClick={onCancel}>Annuleren</Button>
        <Button onClick={onSave} disabled={saving}>{saving ? 'Opslaan…' : 'Opslaan'}</Button>
      </div>
    </>
  );
}

function useSave(onSaved) {
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  async function run(fn) {
    setSaving(true); setError(null);
    try { onSaved(await fn()); } catch (err) { setError(message(err)); }
    setSaving(false);
  }
  return { saving, error, run };
}

function StrategyForm({ strategy, onSaved, onCancel }) {
  const [form, setForm] = useState({
    vision: strategy.vision || '', mission: strategy.mission || '',
    core_values: (strategy.core_values || []).join('\n'), plan_title: strategy.plan_title || '',
    plan_start_year: strategy.plan_start_year ?? '', plan_end_year: strategy.plan_end_year ?? '',
    plan_document_id: strategy.plan_document_id ?? '',
  });
  const [documents, setDocuments] = useState([]);
  const { saving, error, run } = useSave(onSaved);
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
  useEffect(() => {
    api('/api/documents?category=beleid').then(d => setDocuments(Array.isArray(d) ? d : [])).catch(() => {});
  }, []);
  const save = () => run(() => api('/api/goals/strategy', {
    method: 'PUT',
    body: JSON.stringify({
      ...form,
      core_values: form.core_values.split('\n').map(v => v.trim()).filter(Boolean),
      plan_document_id: form.plan_document_id || null,
    }),
  }));
  const linkedMissing = form.plan_document_id && !documents.some(d => String(d.id) === String(form.plan_document_id));
  return (
    <div style="display:flex;flex-direction:column;gap:10px">
      <Field label="Visie — waar staat de club over een aantal jaren?">
        <textarea class="form-input" rows={3} maxLength={2000} value={form.vision} onInput={e => set('vision', e.target.value)} placeholder="Een gezonde vereniging waar iedereen zich thuis voelt en…" />
      </Field>
      <AiAssist kind="goal_vision" value={form.vision} onUse={text => set('vision', text)} />
      <Field label="Missie — waarvoor bestaan we, voor wie?">
        <textarea class="form-input" rows={3} maxLength={2000} value={form.mission} onInput={e => set('mission', e.target.value)} />
      </Field>
      <AiAssist kind="goal_mission" value={form.mission} onUse={text => set('mission', text)} />
      <Field label="Kernwaarden — één per regel (max. 10)">
        <textarea class="form-input" rows={4} value={form.core_values} onInput={e => set('core_values', e.target.value)} placeholder={'Respect\nPlezier\nSamen'} />
      </Field>
      <AiAssist kind="goal_values" value={form.core_values} onUse={text => set('core_values', text)} />
      <Field label="Beleidsplan">
        <input class="form-input" maxLength={200} value={form.plan_title} onInput={e => set('plan_title', e.target.value)} placeholder="Beleidsplan 2026–2030" />
      </Field>
      <div style="display:grid;grid-template-columns:1fr 1fr;gap:8px">
        <Field label="Van (jaar)">
          <input class="form-input" type="number" min="2000" max="2100" value={form.plan_start_year} onInput={e => set('plan_start_year', e.target.value)} />
        </Field>
        <Field label="Tot en met (jaar)">
          <input class="form-input" type="number" min="2000" max="2100" value={form.plan_end_year} onInput={e => set('plan_end_year', e.target.value)} />
        </Field>
      </div>
      <Field label="Document uit de Beleidsbibliotheek">
        <select class="form-input" value={form.plan_document_id} onChange={e => set('plan_document_id', e.target.value)}>
          <option value="">Geen document</option>
          {linkedMissing && <option value={form.plan_document_id}>Huidig gekoppeld document</option>}
          {documents.map(d => <option key={d.id} value={d.id}>{d.file_name}</option>)}
        </select>
      </Field>
      <FormActions saving={saving} error={error} onCancel={onCancel} onSave={save} />
    </div>
  );
}

function FocusForm({ initial, factors, onSaved, onCancel }) {
  const [form, setForm] = useState({ title: initial?.title || '', description: initial?.description || '', success_factor: initial?.success_factor || '' });
  const { saving, error, run } = useSave(onSaved);
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const save = () => run(() => api(initial?.id ? `/api/goals/focus-areas/${initial.id}` : '/api/goals/focus-areas', {
    method: initial?.id ? 'PUT' : 'POST', body: JSON.stringify(form),
  }));
  return (
    <div style="display:flex;flex-direction:column;gap:10px">
      <Field label="Speerpunt">
        <input class="form-input" maxLength={200} value={form.title} onInput={e => set('title', e.target.value)} placeholder="Meer en blijvende vrijwilligers" />
      </Field>
      <Field label="Omschrijving">
        <textarea class="form-input" rows={3} maxLength={2000} value={form.description} onInput={e => set('description', e.target.value)} />
      </Field>
      <AiAssist kind="focus_area_description" value={form.description} context={{ title: form.title }} onUse={text => set('description', text)} />
      <Field label="Succesfactor (optioneel)">
        <select class="form-input" value={form.success_factor} onChange={e => set('success_factor', e.target.value)}>
          <option value="">—</option>
          {factors.map(f => <option key={f.key} value={f.key}>{f.label}</option>)}
        </select>
      </Field>
      <FormActions saving={saving} error={error} onCancel={onCancel} onSave={save} />
    </div>
  );
}

const ownerValue = g => (g?.owner_commission_id ? `c:${g.owner_commission_id}` : g?.owner_section_id ? `s:${g.owner_section_id}` : '');

function GoalForm({ initial, focusAreas, owners, members, onSaved, onCancel }) {
  const [form, setForm] = useState({
    focus_area_id: initial?.focus_area_id || '', title: initial?.title || '', description: initial?.description || '',
    owner: ownerValue(initial), target_date: initial?.target_date || '', status: initial?.status || 'op_schema',
    status_note: initial?.status_note || '', measure_label: initial?.measure_label || '',
    baseline_value: initial?.baseline_value ?? '', target_value: initial?.target_value ?? '', current_value: initial?.current_value ?? '',
    contact_user_id: initial?.contact_user_id ?? '', stopped: !!initial?.stopped_at,
  });
  const { saving, error, run } = useSave(onSaved);
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const save = () => {
    const [kind, oid] = form.owner.split(':');
    const { owner, ...rest } = form;
    const body = { ...rest, owner_section_id: kind === 's' ? Number(oid) : null, owner_commission_id: kind === 'c' ? Number(oid) : null, contact_user_id: form.contact_user_id || null };
    return run(() => api(initial?.id ? `/api/goals/${initial.id}` : '/api/goals', { method: initial?.id ? 'PUT' : 'POST', body: JSON.stringify(body) }));
  };
  return (
    <div style="display:flex;flex-direction:column;gap:10px">
      <Field label="Speerpunt">
        <select class="form-input" value={form.focus_area_id} onChange={e => set('focus_area_id', e.target.value)}>
          <option value="">Kies…</option>
          {focusAreas.map(f => <option key={f.id} value={f.id}>{f.title}</option>)}
        </select>
      </Field>
      <Field label="Doel">
        <input class="form-input" maxLength={200} value={form.title} onInput={e => set('title', e.target.value)} placeholder="In 2028 heeft elk team twee vaste vrijwilligers" />
      </Field>
      <Field label="Omschrijving">
        <textarea class="form-input" rows={2} maxLength={4000} value={form.description} onInput={e => set('description', e.target.value)} />
      </Field>
      <AiAssist kind="goal_description" value={form.description} onUse={text => set('description', text)}
        context={{ title: form.title, focus: focusAreas.find(f => String(f.id) === String(form.focus_area_id))?.title || '' }}
        extra={[{ id: 'smart', label: 'Maak concreet en meetbaar', needsText: true }]} />
      <div style="display:grid;grid-template-columns:1fr 1fr;gap:8px">
        <Field label="Eigenaar">
          <select class="form-input" value={form.owner} onChange={e => set('owner', e.target.value)}>
            <option value="">—</option>
            {owners.map(s => [
              <option key={`s${s.id}`} value={`s:${s.id}`}>{s.name}</option>,
              ...s.commissions.map(c => <option key={`c${c.id}`} value={`c:${c.id}`}>{`  – ${c.name}`}</option>),
            ])}
          </select>
        </Field>
        <Field label="Streefdatum">
          <input class="form-input" type="date" value={form.target_date} onInput={e => set('target_date', e.target.value)} />
        </Field>
      </div>
      <Field label="Contactpersoon (krijgt een herinnering als de voortgang een kwartaal niet is bijgewerkt)">
        <select class="form-input" value={form.contact_user_id} onChange={e => set('contact_user_id', e.target.value)}>
          <option value="">Geen</option>
          {form.contact_user_id && !members.some(m => String(m.id) === String(form.contact_user_id)) && (
            <option value={form.contact_user_id}>{initial?.contact_name || 'Oud-lid'} (geen lid meer — kies iemand anders)</option>
          )}
          {members.map(m => <option key={m.id} value={m.id}>{m.display_name}</option>)}
        </select>
      </Field>
      <div style="display:grid;grid-template-columns:1fr 2fr;gap:8px">
        <Field label="Status">
          <select class="form-input" value={form.status} onChange={e => set('status', e.target.value)}>
            {Object.entries(GOAL_STATUS).map(([k, s]) => <option key={k} value={k}>{s.label}</option>)}
          </select>
        </Field>
        <Field label="Toelichting op de status">
          <input class="form-input" maxLength={1000} value={form.status_note} onInput={e => set('status_note', e.target.value)} placeholder="Wat gaat goed, waar is hulp nodig?" />
        </Field>
      </div>
      <Field label="Meetwaarde (optioneel)">
        <input class="form-input" maxLength={100} value={form.measure_label} onInput={e => set('measure_label', e.target.value)} placeholder="Aantal actieve vrijwilligers" />
      </Field>
      <div style="display:grid;grid-template-columns:1fr 1fr 1fr;gap:8px">
        <Field label="Nulmeting"><input class="form-input" type="number" step="any" value={form.baseline_value} onInput={e => set('baseline_value', e.target.value)} /></Field>
        <Field label="Streefwaarde"><input class="form-input" type="number" step="any" value={form.target_value} onInput={e => set('target_value', e.target.value)} /></Field>
        <Field label="Nu"><input class="form-input" type="number" step="any" value={form.current_value} onInput={e => set('current_value', e.target.value)} /></Field>
      </div>
      {initial?.id && (
        <label style="display:flex;gap:8px;align-items:center;font-size:14px">
          <input type="checkbox" checked={form.stopped} onChange={e => set('stopped', e.target.checked)} />
          Dit doel is gestopt (verdwijnt uit het overzicht, de geschiedenis blijft)
        </label>
      )}
      <FormActions saving={saving} error={error} onCancel={onCancel} onSave={save} />
    </div>
  );
}

const LINK_TYPES = { activity: 'Jaarplanning', process: 'Proces', task: 'Taak', decision: 'Besluit' };
const LINK_ERRORS = { already_linked: 'Dit is al gekoppeld.', invalid_item: 'Dat kun je niet koppelen (of je mag het niet zien).', invalid_type: 'Kies een soort werk.', goal_stopped: 'Dit doel is gestopt.' };

function ProgressBar({ pct, color, label }) {
  return (
    <div style="flex:1;min-width:120px;display:flex;align-items:center;gap:6px">
      <div role="progressbar" aria-valuemin="0" aria-valuemax="100" aria-valuenow={pct} aria-label={label} style="flex:1;height:8px;background:var(--color-border);border-radius:4px;overflow:hidden">
        <div style={`width:${pct}%;height:100%;background:${color}`} />
      </div>
      <span class="text-sm" style="font-weight:600;width:36px;text-align:right">{pct}%</span>
    </div>
  );
}

// Work linked to a goal: what this viewer may see, plus a count of what they can't.
// Editors link and unlink here; they can only pick work they can see themselves.
function LinkedWork({ goal, canEdit, onChanged }) {
  const [open, setOpen] = useState(false);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [picking, setPicking] = useState(false);
  const [type, setType] = useState('task');
  const [search, setSearch] = useState('');
  // Why the type is kept with the results: after switching type the old list is still
  // on screen for a moment, and a click there must link what it shows
  const [options, setOptions] = useState({ type, items: null });
  const load = () => api(`/api/goals/${goal.id}/links`).then(d => { setData(d); setError(null); }).catch(err => setError(message(err)));
  useEffect(() => { if (open) load(); }, [open, goal.id]);
  useEffect(() => {
    if (!picking) return undefined;
    let stale = false;
    setOptions(o => (o.type === type ? o : { type, items: null }));
    const timer = setTimeout(() => {
      api(`/api/goals/link-options?type=${type}&search=${encodeURIComponent(search.trim())}`)
        .then(items => { if (!stale) setOptions({ type, items }); }).catch(() => {});
    }, 250);
    return () => { stale = true; clearTimeout(timer); };
  }, [picking, type, search]);

  const linked = new Set((data?.items || []).map(i => `${i.type}:${i.id}`));
  async function run(fn) {
    setError(null);
    try { await fn(); await load(); onChanged(); } catch (err) { setError(LINK_ERRORS[err.code] || message(err)); }
  }
  const add = (itemType, item) => run(() => api(`/api/goals/${goal.id}/links`, { method: 'POST', body: JSON.stringify({ type: itemType, item_id: item.id }) }));
  const remove = item => run(() => api(`/api/goals/${goal.id}/links/${item.link_id}`, { method: 'DELETE' }));

  const count = goal.progress?.links || 0;
  if (!count && !canEdit) return null;
  return (
    <div style="min-width:0;max-width:100%">
      <button type="button" onClick={() => setOpen(o => !o)} aria-expanded={open}
        style="background:none;border:none;padding:0;cursor:pointer;font-size:13px;color:var(--color-primary)">
        {open ? '▾' : '▸'} Gekoppeld werk{count ? ` (${count})` : ''}
      </button>
      {open && (
        <div style="margin-top:6px;display:flex;flex-direction:column;gap:4px;padding-left:12px;border-left:2px solid var(--color-border);min-width:0;overflow:hidden">
          {!data && !error && <div class="text-sm text-muted">Laden…</div>}
          {data?.items.map(i => (
            <div key={`${i.type}:${i.id}`} style="display:flex;align-items:center;gap:8px;font-size:13px;min-width:0">
              <span class="text-muted" style="width:86px;flex-shrink:0">{LINK_TYPES[i.type]}</span>
              {i.inactive
                ? <span class="text-muted" style="flex:1;min-width:0;font-style:italic">{i.type === 'decision' ? 'Ingetrokken besluit' : 'Activiteit die niet meer actief is'}</span>
                : <a href={i.url} style={`flex:1;min-width:0;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;${i.done ? 'text-decoration:line-through;color:var(--color-text-muted)' : ''}`}>{i.title}</a>}
              {i.done && <span aria-label="afgerond">✓</span>}
              {canEdit && <button type="button" onClick={() => remove(i)} aria-label={`${i.title || LINK_TYPES[i.type]} ontkoppelen`} style="background:none;border:none;cursor:pointer;color:var(--color-text-muted)">✕</button>}
            </div>
          ))}
          {data?.items.length === 0 && !data.hidden_count && <div class="text-sm text-muted">Nog niets gekoppeld.</div>}
          {data?.hidden_count > 0 && <div class="text-sm text-muted">+ {data.hidden_count} {data.hidden_count === 1 ? 'item' : 'items'} dat je niet kunt zien</div>}
          {error && <div class="text-sm" style="color:var(--color-danger)">{error}</div>}
          {canEdit && !picking && <div><Button size="sm" variant="secondary" onClick={() => setPicking(true)}>+ Werk koppelen</Button></div>}
          {canEdit && picking && (
            <div style="display:flex;flex-direction:column;gap:6px;margin-top:4px;min-width:0">
              <div style="display:flex;gap:6px;flex-wrap:wrap">
                <select class="form-input" style="width:auto;height:32px;font-size:13px" value={type} onChange={e => setType(e.target.value)} aria-label="Soort werk">
                  {Object.entries(LINK_TYPES).map(([k, l]) => <option key={k} value={k}>{l}</option>)}
                </select>
                <input class="form-input" style="flex:1;min-width:140px;height:32px;font-size:13px" value={search} onInput={e => setSearch(e.target.value)} placeholder="Zoeken…" aria-label="Zoek werk om te koppelen" />
                <Button size="sm" variant="secondary" onClick={() => setPicking(false)}>Klaar</Button>
              </div>
              {options.items === null && <div class="text-sm text-muted">Zoeken…</div>}
              {(() => {
                const free = (options.items || []).filter(o => !linked.has(`${options.type}:${o.id}`)).slice(0, 10);
                if (options.items && !free.length) return <div class="text-sm text-muted">Niets (meer) gevonden dat je kunt koppelen.</div>;
                return free.map(o => (
                  <div key={`${options.type}:${o.id}`} style="display:flex;align-items:center;gap:8px;font-size:13px;min-width:0">
                    <span style="flex:1;min-width:0;overflow:hidden;text-overflow:ellipsis;white-space:nowrap">{o.title}</span>
                    <Button size="sm" onClick={() => add(options.type, o)}>Koppelen</Button>
                  </div>
                ));
              })()}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

const SOURCE_LABELS = { update: 'Voortgang', edit: 'Doel aangepast', stopped: 'Gestopt', resumed: 'Weer opgepakt' };
const fmtDateTime = v => (v ? new Date(`${v.replace(' ', 'T')}${v.includes('T') ? '' : 'Z'}`).toLocaleDateString('nl-NL', { day: 'numeric', month: 'short', year: 'numeric' }) : null);

// "Voortgang bijwerken": status, current value and a note; also for members of the owner
function UpdateForm({ goal, onSaved, onCancel }) {
  const [form, setForm] = useState({ status: goal.status, current_value: goal.current_value ?? '', note: '' });
  const { saving, error, run } = useSave(onSaved);
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const save = () => run(() => api(`/api/goals/${goal.id}/updates`, { method: 'POST', body: JSON.stringify(form) }));
  return (
    <div style="display:flex;flex-direction:column;gap:10px">
      <div class="text-sm text-muted">{goal.title}</div>
      <Field label="Hoe staat het ervoor?">
        <select class="form-input" value={form.status} onChange={e => set('status', e.target.value)}>
          {Object.entries(GOAL_STATUS).map(([k, st]) => <option key={k} value={k}>{st.label}</option>)}
        </select>
      </Field>
      {goal.measure_label && (
        <Field label={`${goal.measure_label} nu (was ${fmtNum(goal.current_value)}, doel ${fmtNum(goal.target_value)})`}>
          <input class="form-input" type="number" step="any" value={form.current_value} onInput={e => set('current_value', e.target.value)} />
        </Field>
      )}
      <Field label="Toelichting">
        <textarea class="form-input" rows={3} maxLength={1000} value={form.note} onInput={e => set('note', e.target.value)} placeholder="Wat is er bereikt, wat staat er nog open, waar is hulp nodig?" />
      </Field>
      <AiAssist kind="goal_update_note" value={form.note} context={{ title: goal.title }} onUse={text => set('note', text)} />
      <FormActions saving={saving} error={error} onCancel={onCancel} onSave={save} />
    </div>
  );
}

function History({ goal }) {
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState(null);
  useEffect(() => {
    if (!open) return;
    api(`/api/goals/${goal.id}/updates`).then(setItems).catch(() => setItems([]));
  }, [open, goal.id, goal.last_update_at]);
  if (!goal.last_update_at) return null;
  return (
    <div style="min-width:0">
      <button type="button" onClick={() => setOpen(o => !o)} aria-expanded={open}
        style="background:none;border:none;padding:0;cursor:pointer;font-size:13px;color:var(--color-primary)">
        {open ? '▾' : '▸'} Geschiedenis
      </button>
      {open && (
        <div style="margin-top:6px;display:flex;flex-direction:column;gap:6px;padding-left:12px;border-left:2px solid var(--color-border)">
          {!items && <div class="text-sm text-muted">Laden…</div>}
          {items?.map(u => (
            <div key={u.id} style="font-size:13px;display:flex;flex-direction:column;gap:2px">
              <div style="display:flex;gap:6px;align-items:center;flex-wrap:wrap">
                <span class="text-muted">{fmtDateTime(u.created_at)}</span>
                <StatusPill status={u.status} />
                <span class="text-muted">{SOURCE_LABELS[u.source]}{u.created_by_name ? ` · ${u.created_by_name}` : ''}</span>
                {goal.measure_label && u.current_value !== null && <span>{goal.measure_label}: <strong>{fmtNum(u.current_value)}</strong></span>}
              </div>
              {u.note && <div style="white-space:pre-line">{u.note}</div>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function GoalRow({ goal, canEdit, onEdit, onDelete, onChanged, onUpdate }) {
  const pct = measurePct(goal);
  const owner = goal.owner_commission_name || goal.owner_section_name;
  const s = GOAL_STATUS[goal.status] || GOAL_STATUS.op_schema;
  return (
    <div style={`padding:10px 12px;border:1px solid var(--color-border);border-left:4px solid ${goal.stopped_at ? 'var(--color-border)' : s.color};border-radius:8px;display:flex;flex-direction:column;gap:6px;min-width:0${goal.stopped_at ? ';opacity:.75' : ''}`}>
      <div style="display:flex;justify-content:space-between;align-items:flex-start;gap:8px;flex-wrap:wrap">
        <div style="min-width:0;flex:1 1 200px">
          <div style="font-weight:600;font-size:14px">{goal.title}</div>
          <div class="text-sm text-muted">{[owner, goal.target_date && `streefdatum ${fmtDate(goal.target_date)}`, goal.contact_name && `contact: ${goal.contact_name}`, goal.last_update_at && `bijgewerkt ${fmtDateTime(goal.last_update_at)}`].filter(Boolean).join(' · ')}</div>
        </div>
        <div style="display:flex;gap:6px;align-items:center;flex-shrink:0">
          {goal.stopped_at
            ? <span style="font-size:11px;font-weight:600;padding:2px 8px;border-radius:10px;background:var(--color-bg-secondary);color:var(--color-text-muted);border:1px solid var(--color-border)">Gestopt</span>
            : <StatusPill status={goal.status} />}
          {goal.can_update && <Button size="sm" onClick={onUpdate}>Voortgang bijwerken</Button>}
          {canEdit && <Button size="sm" variant="secondary" onClick={onEdit}>Bewerken</Button>}
          {canEdit && <Button size="sm" variant="secondary" onClick={onDelete} aria-label="Doel verwijderen">✕</Button>}
        </div>
      </div>
      {goal.description && <div class="text-sm" style="white-space:pre-line">{goal.description}</div>}
      {goal.measure_label && (
        <div style="display:flex;align-items:center;gap:8px;flex-wrap:wrap">
          <span class="text-sm" style="min-width:0">{goal.measure_label}: <strong>{fmtNum(goal.current_value)}</strong> (van {fmtNum(goal.baseline_value)} naar {fmtNum(goal.target_value)})</span>
          {pct !== null && <ProgressBar pct={pct} color={s.color} label={`${goal.measure_label}: ${pct}% van het doel`} />}
        </div>
      )}
      {goal.progress?.total > 0 && (
        <div style="display:flex;align-items:center;gap:8px;flex-wrap:wrap">
          <span class="text-sm">Werk: <strong>{goal.progress.done}</strong> van {goal.progress.total} afgerond</span>
          <ProgressBar pct={goal.progress.pct} color="var(--color-primary)" label={`Gekoppeld werk: ${goal.progress.pct}% afgerond`} />
        </div>
      )}
      {goal.status_note && <div class="text-sm text-muted" style="font-style:italic">“{goal.status_note}”</div>}
      <LinkedWork goal={goal} canEdit={canEdit} onChanged={onChanged} />
      <History goal={goal} />
    </div>
  );
}

export default function GoalsView() {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [modal, setModal] = useState(null); // { kind: 'strategy'|'focus'|'goal'|'update', item }
  const [showStopped, setShowStopped] = useState(false);

  const load = () => api('/api/goals').then(setData).catch(err => setError(
    err.code === 'forbidden' ? 'Je hebt geen toegang tot de visie en doelen van deze club.' : message(err)));
  useEffect(() => { load(); }, []);

  if (error && !data) return <div class="alert alert-error">{error}</div>;
  if (!data) return <div class="text-muted" style="padding:40px;text-align:center">Laden…</div>;

  const { strategy, focus_areas: areas, success_factors: factors, can_edit: canEdit, can_read_documents: canReadDocs, owners } = data;
  const factorLabel = key => factors.find(f => f.key === key)?.label;
  // Stopped goals leave the overview (and the counts); their history stays
  const allGoals = areas.flatMap(a => a.goals).filter(g => !g.stopped_at);
  const stoppedCount = areas.flatMap(a => a.goals).length - allGoals.length;
  const shownGoals = a => a.goals.filter(g => showStopped || !g.stopped_at);
  const counts = Object.keys(GOAL_STATUS).map(k => [k, allGoals.filter(g => g.status === k).length]).filter(([, n]) => n > 0);
  const hasPlan = strategy.plan_title || strategy.plan_start_year || strategy.plan_end_year || strategy.plan_document_id;
  const hasStrategy = strategy.vision || strategy.mission || strategy.core_values.length || hasPlan;
  const saved = () => { setModal(null); load(); };

  async function run(fn) {
    setError(null);
    try { await fn(); load(); } catch (err) { setError(message(err)); }
  }
  const removeFocus = a => confirm(`Speerpunt "${a.title}" verwijderen?`) && run(() => api(`/api/goals/focus-areas/${a.id}`, { method: 'DELETE' }));
  const removeGoal = g => confirm(`Doel "${g.title}" verwijderen?`) && run(() => api(`/api/goals/${g.id}`, { method: 'DELETE' }));
  const move = (a, direction) => run(() => api(`/api/goals/focus-areas/${a.id}/move`, { method: 'POST', body: JSON.stringify({ direction }) }));

  const period = [strategy.plan_start_year, strategy.plan_end_year].filter(Boolean).join('–');

  return (
    <div>
      <div class="page-header" style="margin-bottom:20px;display:flex;justify-content:space-between;align-items:center;gap:12px;flex-wrap:wrap">
        <h1 class="page-title">🎯 Visie &amp; doelen</h1>
        {canEdit && (
          <div style="display:flex;gap:8px;flex-wrap:wrap">
            <Button variant="secondary" onClick={() => setModal({ kind: 'strategy' })}>Visie bewerken</Button>
            <Button variant="secondary" onClick={() => setModal({ kind: 'focus' })}>+ Speerpunt</Button>
            {areas.length > 0 && <Button onClick={() => setModal({ kind: 'goal' })}>+ Doel</Button>}
          </div>
        )}
      </div>

      {error && <div class="alert alert-error mb-3">{error}</div>}

      {!hasStrategy && areas.length === 0 ? (
        <Card>
          <div style="text-align:center;padding:24px;display:flex;flex-direction:column;gap:8px;align-items:center">
            <div style="font-size:32px">🎯</div>
            <div style="font-weight:600">Nog geen visie en doelen vastgelegd</div>
            <div class="text-muted text-sm" style="max-width:520px">
              Leg vast waar de club naartoe wil: een visie, de speerpunten uit het beleidsplan en concrete doelen met een eigenaar.
              Zo ziet iedereen waar het werk aan bijdraagt.
            </div>
            {canEdit && <Button onClick={() => setModal({ kind: 'strategy' })}>Begin met de visie</Button>}
          </div>
        </Card>
      ) : (
        <div style="display:flex;flex-direction:column;gap:16px">
          {hasStrategy && (
            <Card title="Onze koers">
              <div style="display:flex;flex-direction:column;gap:12px">
                {strategy.vision && (
                  <div>
                    <div class="text-sm text-muted" style="font-weight:600;text-transform:uppercase;letter-spacing:.04em">Visie</div>
                    <div style="font-size:16px;white-space:pre-line">{strategy.vision}</div>
                  </div>
                )}
                {strategy.mission && (
                  <div>
                    <div class="text-sm text-muted" style="font-weight:600;text-transform:uppercase;letter-spacing:.04em">Missie</div>
                    <div style="white-space:pre-line">{strategy.mission}</div>
                  </div>
                )}
                {strategy.core_values.length > 0 && (
                  <div style="display:flex;gap:6px;flex-wrap:wrap">
                    {strategy.core_values.map((v, i) => <span key={i} style="padding:3px 10px;border-radius:12px;background:var(--color-bg-secondary);border:1px solid var(--color-border);font-size:13px">{v}</span>)}
                  </div>
                )}
                {hasPlan && (
                  <div class="text-sm">
                    📄 {strategy.plan_title || 'Beleidsplan'}{period && ` (${period})`}
                    {strategy.plan_document_id && canReadDocs && <> · <a href={`/api/documents/${strategy.plan_document_id}/preview`} target="_blank" rel="noopener" onClick={e => fileLinkClick(e, strategy.plan_title)}>document openen</a></>}
                  </div>
                )}
              </div>
            </Card>
          )}

          {stoppedCount > 0 && (
            <label style="display:flex;gap:6px;align-items:center;font-size:13px;color:var(--color-text-muted)">
              <input type="checkbox" checked={showStopped} onChange={e => setShowStopped(e.target.checked)} />
              Toon gestopte doelen ({stoppedCount})
            </label>
          )}

          {counts.length > 0 && (
            <div style="display:flex;gap:8px;flex-wrap:wrap;align-items:center">
              <span class="text-sm text-muted">{allGoals.length} {allGoals.length === 1 ? 'doel' : 'doelen'}:</span>
              {counts.map(([k, n]) => <span key={k} style="display:flex;align-items:center;gap:4px"><StatusPill status={k} /><span class="text-sm">{n}</span></span>)}
            </div>
          )}

          {areas.map((a, i) => (
            <Card
              key={a.id}
              title={a.title}
              actions={canEdit && [
                <Button key="up" size="sm" variant="secondary" disabled={i === 0} onClick={() => move(a, 'up')} aria-label="Omhoog">↑</Button>,
                <Button key="down" size="sm" variant="secondary" disabled={i === areas.length - 1} onClick={() => move(a, 'down')} aria-label="Omlaag">↓</Button>,
                <Button key="edit" size="sm" variant="secondary" onClick={() => setModal({ kind: 'focus', item: a })}>Bewerken</Button>,
                <Button key="del" size="sm" variant="secondary" onClick={() => removeFocus(a)} aria-label="Speerpunt verwijderen">✕</Button>,
              ]}
            >
              <div style="display:flex;flex-direction:column;gap:8px">
                {(a.success_factor || a.description) && (
                  <div style="display:flex;gap:8px;align-items:flex-start;flex-wrap:wrap">
                    {a.success_factor && <span style="font-size:11px;padding:2px 8px;border-radius:10px;background:var(--color-primary);color:#fff;font-weight:500">{factorLabel(a.success_factor)}</span>}
                    {a.description && <div class="text-sm" style="white-space:pre-line;flex:1;min-width:200px">{a.description}</div>}
                  </div>
                )}
                {shownGoals(a).length === 0 && <div class="text-sm text-muted">{a.goals.length ? 'Alle doelen bij dit speerpunt zijn gestopt.' : 'Nog geen doelen bij dit speerpunt.'}</div>}
                {shownGoals(a).map(g => (
                  <GoalRow key={g.id} goal={g} canEdit={canEdit} onEdit={() => setModal({ kind: 'goal', item: g })} onDelete={() => removeGoal(g)}
                    onChanged={load} onUpdate={() => setModal({ kind: 'update', item: g })} />
                ))}
                {canEdit && (
                  <div><Button size="sm" variant="secondary" onClick={() => setModal({ kind: 'goal', item: { focus_area_id: a.id } })}>+ Doel bij dit speerpunt</Button></div>
                )}
              </div>
            </Card>
          ))}
        </div>
      )}

      <Modal isOpen={modal?.kind === 'strategy'} title="Visie en beleidsplan" onClose={() => setModal(null)}>
        {modal?.kind === 'strategy' && <StrategyForm strategy={strategy} onSaved={saved} onCancel={() => setModal(null)} />}
      </Modal>
      <Modal isOpen={modal?.kind === 'focus'} title={modal?.item ? 'Speerpunt bewerken' : 'Nieuw speerpunt'} onClose={() => setModal(null)}>
        {modal?.kind === 'focus' && <FocusForm initial={modal.item} factors={factors} onSaved={saved} onCancel={() => setModal(null)} />}
      </Modal>
      <Modal isOpen={modal?.kind === 'goal'} title={modal?.item?.id ? 'Doel bewerken' : 'Nieuw doel'} onClose={() => setModal(null)}>
        {modal?.kind === 'goal' && <GoalForm initial={modal.item} focusAreas={areas} owners={owners} members={data.members || []} onSaved={saved} onCancel={() => setModal(null)} />}
      </Modal>
      <Modal isOpen={modal?.kind === 'update'} title="Voortgang bijwerken" onClose={() => setModal(null)}>
        {modal?.kind === 'update' && <UpdateForm goal={modal.item} onSaved={saved} onCancel={() => setModal(null)} />}
      </Modal>
    </div>
  );
}
